import { useState, useRef, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { Pencil, Loader2 } from 'lucide-react'
import { FileExplorerItem } from '@features/file-explorer/types'
import Modal from './ui/Modal'

interface RenameModalProps {
  isOpen: boolean
  item: FileExplorerItem | null
  onClose: () => void
}

export default function RenameModal({
  isOpen,
  item,
  onClose,
}: RenameModalProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  // Initialize form when item changes
  useEffect(() => {
    if (item) {
      setName(item.name)
      setDescription(item.description || '')
    }
  }, [item])

  // Auto-focus the name input when modal opens
  useEffect(() => {
    if (isOpen && nameInputRef.current) {
      nameInputRef.current.focus()
      // Select the text for easy editing
      nameInputRef.current.select()
    }
  }, [isOpen])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!item || !name.trim()) {
      alert(t('fileExplorer.rename.errors.nameRequired'))
      return
    }

    setIsSubmitting(true)

    const endpoint = item.is_folder ? `/folders/${item.id}` : `/documents/${item.id}`
    const dataKey = item.is_folder ? 'folder' : 'document'

    router.patch(
      endpoint,
      {
        [dataKey]: {
          name: name.trim(),
          description: description.trim() || undefined,
        },
      },
      {
        only: item.is_folder ? ['items', 'folder_tree', 'breadcrumbs', 'flash'] : ['items', 'flash'],
        preserveScroll: true,
        onSuccess: () => {
          handleClose()
        },
        onError: (errors) => {
          console.error('Rename error:', errors)
          setIsSubmitting(false)
        },
        onFinish: () => {
          setIsSubmitting(false)
        },
      }
    )
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setName('')
      setDescription('')
      onClose()
    }
  }

  if (!item) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      title={t(`fileExplorer.rename.title${item.is_folder ? 'Folder' : 'Document'}`)}
      icon={<Pencil className="w-6 h-6 text-primary" />}
      disabled={isSubmitting}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            className="btn btn-ghost"
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="rename-form"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Pencil className="w-4 h-4" />
            )}
            {t('fileExplorer.rename.save')}
          </button>
        </>
      }
    >
      <form id="rename-form" onSubmit={handleSubmit} className="space-y-5">
        {/* Name Field */}
        <div className="form-control">
          <label className="label pb-1">
            <span className="label-text font-medium">
              {t('fileExplorer.rename.name')}
              <span className="text-error ml-1">*</span>
            </span>
          </label>
          <input
            ref={nameInputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('fileExplorer.rename.namePlaceholder')}
            className="input input-bordered w-full focus:input-primary"
            required
            disabled={isSubmitting}
            maxLength={255}
          />
        </div>

        {/* Description Field */}
        <div className="form-control">
          <label className="label pb-1">
            <span className="label-text font-medium">
              {t('fileExplorer.rename.description')}
            </span>
            <span className="label-text-alt text-base-content/50">
              {t('common.optional')}
            </span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('fileExplorer.rename.descriptionPlaceholder')}
            className="textarea textarea-bordered w-full focus:textarea-primary resize-none"
            rows={3}
            disabled={isSubmitting}
          />
        </div>
      </form>
    </Modal>
  )
}
